import React from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaPalette, FaBullseye, FaLock, FaRocket } from 'react-icons/fa';

const VisualBusinessJourney = () => {
    const stages = [
        {
            title: "Market Research & Tech Feasibility Analysis",
            description: "Validate your IT product or service idea with in-depth market research, competitor benchmarking, and technical feasibility analysis for global success.",
            icon: <FaChartLine className="text-3xl text-blue-500" />,
            image: "Stage 1 Market Research & Tech Feasibility Analysis.webp",
            yourRole: "We analyze current technologies, industry demands, and competition to shape your IT solution strategy across India, USA, Australia, and Europe.",
            color: "from-blue-50 to-blue-100",
            darkColor: "from-white-800 to-pink-900/30"
        },
        {
            title: "Brand Identity & Responsive Web Design",
            description: "Craft a compelling brand identity with responsive websites, seamless UI/UX design, and SEO-optimized code — built for user trust and conversions.",
            icon: <FaPalette className="text-3xl text-purple-500" />,
            image: "Brand Identity & Responsive Web Design.webp",
            yourRole: "We deliver scalable, mobile-first websites and brand systems tailored for SaaS platforms, tech startups, and enterprise portals.",
            color: "from-purple-50 to-purple-100",
            darkColor: "from-white-800 to-purple-900/30"
        },
        {
            title: "Digital Marketing & Tech Lead Generation",
            description: "Accelerate online visibility and leads through technical SEO, paid ads, and growth-focused marketing funnels tailored for IT companies.",
            icon: <FaBullseye className="text-3xl text-amber-500" />,
            image: "Stage 3 Digital Marketing & Tech Lead Generation.webp",
            yourRole: "We drive traffic and conversions through targeted campaigns for SaaS, mobile apps, and custom software services worldwide.",
            color: "from-amber-50 to-amber-100",
            darkColor: "from-white-800 to-amber-900/30"
        },
        {
            title: "Secure Payment Gateway & API Integration",
            description: "Implement secure, global payment systems with end-to-end encryption, multi-currency support, and seamless third-party API integration.",
            icon: <FaLock className="text-3xl text-cyan-500" />,
            image: "Stage 4 Secure Payment Gateway & API Integration.webp",
            yourRole: "We integrate PCI-DSS compliant gateways like Stripe, Razorpay, and PayPal to ensure safe, smooth, and scalable transactions.",
            color: "from-cyan-50 to-cyan-100",
            darkColor: "from-white-800 to-cyan-900/30"
        },
        {
            title: "Data-Driven Scaling & Automation",
            description: "Fuel your IT business growth with AI-powered analytics, marketing automation, and performance optimization for global scalability.",
            icon: <FaRocket className="text-3xl text-indigo-500" />,
            image: "Data-Driven Scaling & Automation.webp",
            yourRole: "We optimize cloud infrastructure, implement CRMs, and deploy automation tools to support your global expansion with minimal friction.",
            color: "from-indigo-50 to-indigo-100",
            darkColor: "from-white-800 to-indigo-900/30"
        }
    ];


    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute inset-0 overflow-hidden opacity-10 dark:opacity-5">
                <div className="absolute top-0 left-0 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000" />
                <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Animated Heading */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-full mb-4">
                        END-TO-END DIGITAL TRANSFORMATION
                    </span>
                    <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white">
                        Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">Technology Journey</span>
                    </h2>
                    <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        From initial concept to global scaling - we provide comprehensive IT solutions at every stage
                    </p>
                </motion.div>

                {/* Interactive Timeline */}
                <div className="space-y-16 lg:space-y-24">
                    {stages.map((stage, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            className="relative group"
                        >
                            {/* Timeline connector */}
                            {index !== 0 && (
                                <div className="hidden lg:block absolute left-1/2 top-0 w-1 h-full bg-gradient-to-b from-blue-300 to-purple-300 -translate-x-1/2 -translate-y-16" />
                            )}

                            <div className={`flex flex-col lg:flex-row ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12`}>
                                {/* Image with hover effect */}
                                <div className="lg:w-1/2 mb-8 lg:mb-0 overflow-hidden rounded-2xl shadow-2xl relative">
                                    <img
                                        src={stage.image}
                                        alt={stage.title}
                                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                                        <span className="text-white text-lg font-medium">{stage.title}</span>
                                    </div>
                                </div>

                                {/* Text Content */}
                                <div className={`lg:w-1/2 p-8 lg:p-10 rounded-2xl bg-gradient-to-br ${stage.color} dark:bg-gradient-to-br ${stage.darkColor} shadow-lg`}>
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                            {stage.icon}
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">Stage {index + 1}</span>
                                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stage.title}</h3>
                                        </div>
                                    </div>
                                    <p className="text-gray-700 dark:text-gray-300 mb-6">{stage.description}</p>
                                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                                        <p className="font-semibold text-indigo-600 dark:text-indigo-400 mb-2">Our Expertise:</p>
                                        <p className="text-gray-800 dark:text-gray-200">{stage.yourRole}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Step number */}
                            <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 bg-white dark:bg-gray-800 border-4 border-indigo-500 rounded-full items-center justify-center shadow-xl z-10">
                                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{index + 1}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>


            </div>
        </section>
    );
};

export default VisualBusinessJourney;